import React, { useState, useEffect } from 'react';
import {
  Home,
  Info,
  Phone,
  MessageSquare,
  Plus,
  Trash2,
  Save,
  Globe,
  Loader2,
} from 'lucide-react';
import { showToast } from '../../../../utils/showToast';
import { getPageContent, updatePageContent } from '../../../../services/pageService';
import Loader from '../../../../components/common/Loader';

type Tab = 'home' | 'about' | 'contact' | 'forum';

const ManagePages: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('home');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Page contents states
  const [homeContent, setHomeContent] = useState<any>({
    heroTitle: '',
    heroSubtitle: '',
    heroImage: '',
    features: [],
    stats: [],
  });

  const [aboutContent, setAboutContent] = useState<any>({
    title: '',
    subtitle: '',
    whoWeAre: '',
    whyChooseUs: [],
    team: [],
  });

  const [contactContent, setContactContent] = useState<any>({
    title: '',
    subtitle: '',
    phone: '',
    phoneTimings: '',
    email: '',
    emailTimings: '',
    addressTitle: '',
    addressBody: '',
    officeHours: [],
    mapUrl: '',
    whatsappNumber: '',
  });

  const [forumContent, setForumContent] = useState<any>({
    title: '',
    subtitle: '',
  });

  // Fetch page contents on mount
  useEffect(() => {
    const fetchAllContents = async () => {
      setIsLoading(true);
      try {
        const [home, about, contact, forum] = await Promise.all([
          getPageContent('home').catch(() => ({})),
          getPageContent('about').catch(() => ({})),
          getPageContent('contact').catch(() => ({})),
          getPageContent('forum').catch(() => ({})),
        ]);

        setHomeContent((prev: any) => ({ ...prev, ...home }));
        setAboutContent((prev: any) => ({ ...prev, ...about }));
        setContactContent((prev: any) => ({ ...prev, ...contact }));
        setForumContent((prev: any) => ({ ...prev, ...forum }));
      } catch (error: any) {
        showToast('Failed to load page contents.', { type: 'error' });
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllContents();
  }, []);

  const handleSave = async (tab: Tab) => {
    setIsSaving(true);
    let payload: any = {};
    if (tab === 'home') payload = homeContent;
    if (tab === 'about') payload = aboutContent;
    if (tab === 'contact') payload = contactContent;
    if (tab === 'forum') payload = forumContent;

    try {
      await updatePageContent(tab, payload);
      showToast(`${tab.toUpperCase()} page contents updated successfully!`, { type: 'success' });
    } catch (error: any) {
      showToast(error.message || 'Failed to save changes.', { type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  // Helper functions for features/lists edits
  const handleFeatureChange = (index: number, val: string) => {
    const updatedFeatures = [...homeContent.features];
    updatedFeatures[index] = val;
    setHomeContent({ ...homeContent, features: updatedFeatures });
  };

  const addFeature = () => {
    setHomeContent({
      ...homeContent,
      features: [...(homeContent.features || []), ''],
    });
  };

  const removeFeature = (index: number) => {
    const updatedFeatures = homeContent.features.filter((_: any, idx: number) => idx !== index);
    setHomeContent({ ...homeContent, features: updatedFeatures });
  };

  const handleStatChange = (index: number, field: string, val: string) => {
    const updatedStats = [...homeContent.stats];
    updatedStats[index] = { ...updatedStats[index], [field]: val };
    setHomeContent({ ...homeContent, stats: updatedStats });
  };

  const addStat = () => {
    setHomeContent({
      ...homeContent,
      stats: [...(homeContent.stats || []), { label: '', value: '' }],
    });
  };

  const removeStat = (index: number) => {
    const updatedStats = homeContent.stats.filter((_: any, idx: number) => idx !== index);
    setHomeContent({ ...homeContent, stats: updatedStats });
  };

  // About page list helpers
  const handleWhyChooseChange = (index: number, val: string) => {
    const updatedWhy = [...aboutContent.whyChooseUs];
    updatedWhy[index] = val;
    setAboutContent({ ...aboutContent, whyChooseUs: updatedWhy });
  };

  const addWhyChoose = () => {
    setAboutContent({
      ...aboutContent,
      whyChooseUs: [...(aboutContent.whyChooseUs || []), ''],
    });
  };

  const removeWhyChoose = (index: number) => {
    const updatedWhy = aboutContent.whyChooseUs.filter((_: any, idx: number) => idx !== index);
    setAboutContent({ ...aboutContent, whyChooseUs: updatedWhy });
  };

  const handleTeamChange = (index: number, field: string, val: string) => {
    const updatedTeam = [...aboutContent.team];
    updatedTeam[index] = { ...updatedTeam[index], [field]: val };
    setAboutContent({ ...aboutContent, team: updatedTeam });
  };

  const addTeamMember = () => {
    setAboutContent({
      ...aboutContent,
      team: [...(aboutContent.team || []), { name: '', role: '', image: '' }],
    });
  };

  const removeTeamMember = (index: number) => {
    const updatedTeam = aboutContent.team.filter((_: any, idx: number) => idx !== index);
    setAboutContent({ ...aboutContent, team: updatedTeam });
  };

  // Contact Page list helpers
  const handleOfficeHourChange = (index: number, val: string) => {
    const updatedHours = [...contactContent.officeHours];
    updatedHours[index] = val;
    setContactContent({ ...contactContent, officeHours: updatedHours });
  };

  const addOfficeHour = () => {
    setContactContent({
      ...contactContent,
      officeHours: [...(contactContent.officeHours || []), ''],
    });
  };

  const removeOfficeHour = (index: number) => {
    const updatedHours = contactContent.officeHours.filter((_: any, idx: number) => idx !== index);
    setContactContent({ ...contactContent, officeHours: updatedHours });
  };

  if (isLoading) {
    return (
      <div className="flex min-h-64 items-center justify-center bg-gray-100">
        <Loader />
      </div>
    );
  }

  return (
    <div className="space-y-8 bg-gray-50 p-4 sm:p-6 lg:p-10">
      {/* Title */}
      <div className="flex items-center gap-3">
        <div className="p-2.5 bg-gradient-to-tr from-blue-600 to-indigo-600 rounded-xl text-white shadow-md">
          <Globe className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Manage Public Pages</h1>
          <p className="text-sm text-gray-500">Dynamically update website content for all users.</p>
        </div>
      </div>

      {/* Tabs list */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-1">
        {[
          { id: 'home', label: 'Home Page', icon: Home },
          { id: 'about', label: 'About Us', icon: Info },
          { id: 'contact', label: 'Contact Us', icon: Phone },
          { id: 'forum', label: 'Community Forum', icon: MessageSquare },
        ].map((tab) => {
          const Icon = tab.icon;
          const isSelected = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as Tab)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold rounded-t-xl transition-all duration-300 ${
                isSelected
                  ? 'bg-white border-t border-x border-gray-200 text-blue-600 shadow-[0_-2px_10px_-3px_rgba(0,0,0,0.05)] translate-y-[1px]'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Icon className={`w-4 h-4 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Editor Content Area */}
      <div className="bg-white rounded-2xl shadow-xl ring-1 ring-gray-100 p-6 sm:p-8 border border-gray-200/50">
        {/* TAB 1: HOME PAGE */}
        {activeTab === 'home' && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-800 border-b pb-3">Home Hero Section</h2>
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Hero Title</label>
                <input
                  type="text"
                  value={homeContent.heroTitle || ''}
                  onChange={(e) => setHomeContent({ ...homeContent, heroTitle: e.target.value })}
                  placeholder="e.g. Build Your Career with SAP S/4HANA SD, MM & Mentorship"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Hero Subtitle</label>
                <textarea
                  value={homeContent.heroSubtitle || ''}
                  onChange={(e) => setHomeContent({ ...homeContent, heroSubtitle: e.target.value })}
                  placeholder="Enter a brief, engaging description of SGC platform."
                  rows={3}
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Hero Image URL</label>
                <input
                  type="text"
                  value={homeContent.heroImage || ''}
                  onChange={(e) => setHomeContent({ ...homeContent, heroImage: e.target.value })}
                  placeholder="e.g. /logo.jpg or full url. Leave empty to use default logo."
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300"
                />
              </div>
            </div>

            {/* Features list */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold text-gray-800">Hero Features</h3>
                <button
                  type="button"
                  onClick={addFeature}
                  className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1 transition-colors"
                >
                  <Plus className="w-4 h-4" /> Add Feature
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(homeContent.features || []).map((feature: string, idx: number) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => handleFeatureChange(idx, e.target.value)}
                      placeholder="e.g. Real-time SAP System Access"
                      className="flex-1 px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => removeFeature(idx)}
                      className="p-2.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-xl transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats list */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold text-gray-800">Platform Stats</h3>
                <button
                  type="button"
                  onClick={addStat}
                  className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1 transition-colors"
                >
                  <Plus className="w-4 h-4" /> Add Stat
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(homeContent.stats || []).map((stat: any, idx: number) => (
                  <div key={idx} className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3 relative group">
                    <button
                      type="button"
                      onClick={() => removeStat(idx)}
                      className="absolute top-2 right-2 p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1">Value</label>
                      <input
                        type="text"
                        value={stat.value}
                        onChange={(e) => handleStatChange(idx, 'value', e.target.value)}
                        placeholder="e.g. 2000+ or Hands-on"
                        className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1">Label</label>
                      <input
                        type="text"
                        value={stat.label}
                        onChange={(e) => handleStatChange(idx, 'label', e.target.value)}
                        placeholder="e.g. Students Trained"
                        className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Save Button */}
            <div className="pt-6 border-t flex justify-end">
              <button
                type="button"
                onClick={() => handleSave('home')}
                disabled={isSaving}
                className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold px-8 py-3 rounded-xl shadow-lg hover:shadow-blue-500/25 transition-all duration-300 flex items-center justify-center gap-2 transform hover:-translate-y-0.5"
              >
                {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                <span>Save Home Page Changes</span>
              </button>
            </div>
          </div>
        )}

        {/* TAB 2: ABOUT US */}
        {activeTab === 'about' && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-800 border-b pb-3">About Us Header</h2>
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Title</label>
                <input
                  type="text"
                  value={aboutContent.title || ''}
                  onChange={(e) => setAboutContent({ ...aboutContent, title: e.target.value })}
                  placeholder="e.g. About ShreeGuruCool"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Subtitle</label>
                <input
                  type="text"
                  value={aboutContent.subtitle || ''}
                  onChange={(e) => setAboutContent({ ...aboutContent, subtitle: e.target.value })}
                  placeholder="e.g. Empowering learners with SAP S/4HANA skills to transform careers and businesses."
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Who We Are</label>
                <textarea
                  value={aboutContent.whoWeAre || ''}
                  onChange={(e) => setAboutContent({ ...aboutContent, whoWeAre: e.target.value })}
                  placeholder="Describe your organization's mission and purpose."
                  rows={5}
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300"
                />
              </div>
            </div>

            {/* Why Choose Us */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold text-gray-800">Why Choose Us Points</h3>
                <button
                  type="button"
                  onClick={addWhyChoose}
                  className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1 transition-colors"
                >
                  <Plus className="w-4 h-4" /> Add Point
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(aboutContent.whyChooseUs || []).map((point: string, idx: number) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <input
                      type="text"
                      value={point}
                      onChange={(e) => handleWhyChooseChange(idx, e.target.value)}
                      placeholder="e.g. 100% practical training with real-time case studies"
                      className="flex-1 px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => removeWhyChoose(idx)}
                      className="p-2.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-xl transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Team Members */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold text-gray-800">Team Members</h3>
                <button
                  type="button"
                  onClick={addTeamMember}
                  className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1 transition-colors"
                >
                  <Plus className="w-4 h-4" /> Add Team Member
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {(aboutContent.team || []).map((member: any, idx: number) => (
                  <div key={idx} className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3 relative group">
                    <button
                      type="button"
                      onClick={() => removeTeamMember(idx)}
                      className="absolute top-2 right-2 p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1">Name</label>
                      <input
                        type="text"
                        value={member.name}
                        onChange={(e) => handleTeamChange(idx, 'name', e.target.value)}
                        placeholder="e.g. Gurunatha Reddy Samala"
                        className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1">Role</label>
                      <input
                        type="text"
                        value={member.role}
                        onChange={(e) => handleTeamChange(idx, 'role', e.target.value)}
                        placeholder="e.g. Founder & Proprietor"
                        className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1">Image URL</label>
                      <input
                        type="text"
                        value={member.image}
                        onChange={(e) => handleTeamChange(idx, 'image', e.target.value)}
                        placeholder="e.g. /team/name.jpg or linkedin url"
                        className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Save Button */}
            <div className="pt-6 border-t flex justify-end">
              <button
                type="button"
                onClick={() => handleSave('about')}
                disabled={isSaving}
                className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold px-8 py-3 rounded-xl shadow-lg hover:shadow-blue-500/25 transition-all duration-300 flex items-center justify-center gap-2 transform hover:-translate-y-0.5"
              >
                {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                <span>Save About Us Changes</span>
              </button>
            </div>
          </div>
        )}

        {/* TAB 3: CONTACT US */}
        {activeTab === 'contact' && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-800 border-b pb-3">Contact Us Layout</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Title</label>
                <input
                  type="text"
                  value={contactContent.title || ''}
                  onChange={(e) => setContactContent({ ...contactContent, title: e.target.value })}
                  placeholder="e.g. Contact Us"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Subtitle</label>
                <input
                  type="text"
                  value={contactContent.subtitle || ''}
                  onChange={(e) => setContactContent({ ...contactContent, subtitle: e.target.value })}
                  placeholder="Enter contact description text"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
                <input
                  type="text"
                  value={contactContent.phone || ''}
                  onChange={(e) => setContactContent({ ...contactContent, phone: e.target.value })}
                  placeholder="e.g. +91 77950 06081"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Support Timings</label>
                <input
                  type="text"
                  value={contactContent.phoneTimings || ''}
                  onChange={(e) => setContactContent({ ...contactContent, phoneTimings: e.target.value })}
                  placeholder="e.g. Mon–Sat: 9:30 AM – 6:30 PM"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
                <input
                  type="email"
                  value={contactContent.email || ''}
                  onChange={(e) => setContactContent({ ...contactContent, email: e.target.value })}
                  placeholder="e.g. shreegurucool@gmail.com"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email Support Timings</label>
                <input
                  type="text"
                  value={contactContent.emailTimings || ''}
                  onChange={(e) => setContactContent({ ...contactContent, emailTimings: e.target.value })}
                  placeholder="e.g. 24x7 Email Support"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Address Title</label>
                <input
                  type="text"
                  value={contactContent.addressTitle || ''}
                  onChange={(e) => setContactContent({ ...contactContent, addressTitle: e.target.value })}
                  placeholder="e.g. ShreeGuruCool teKnologies"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">WhatsApp Number (For Link)</label>
                <input
                  type="text"
                  value={contactContent.whatsappNumber || ''}
                  onChange={(e) => setContactContent({ ...contactContent, whatsappNumber: e.target.value })}
                  placeholder="e.g. 917795006081 (with country code, no +)"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Address Body</label>
                <textarea
                  value={contactContent.addressBody || ''}
                  onChange={(e) => setContactContent({ ...contactContent, addressBody: e.target.value })}
                  placeholder="e.g. 2nd Floor, Above Nest Bakery, CKB Layout, Marathalli, Bangalore"
                  rows={3}
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Google Maps Embed iframe Link (src only)</label>
                <input
                  type="text"
                  value={contactContent.mapUrl || ''}
                  onChange={(e) => setContactContent({ ...contactContent, mapUrl: e.target.value })}
                  placeholder="Paste only the https://www.google.com/maps/embed?... URL"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 text-sm"
                />
              </div>
            </div>

            {/* Office Hours */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold text-gray-800">Office Hours Lines</h3>
                <button
                  type="button"
                  onClick={addOfficeHour}
                  className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1 transition-colors"
                >
                  <Plus className="w-4 h-4" /> Add Office Hour Line
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(contactContent.officeHours || []).map((hours: string, idx: number) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <input
                      type="text"
                      value={hours}
                      onChange={(e) => handleOfficeHourChange(idx, e.target.value)}
                      placeholder="e.g. Mon–Fri: 9:30 AM – 6:30 PM"
                      className="flex-1 px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => removeOfficeHour(idx)}
                      className="p-2.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-xl transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Save Button */}
            <div className="pt-6 border-t flex justify-end">
              <button
                type="button"
                onClick={() => handleSave('contact')}
                disabled={isSaving}
                className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold px-8 py-3 rounded-xl shadow-lg hover:shadow-blue-500/25 transition-all duration-300 flex items-center justify-center gap-2 transform hover:-translate-y-0.5"
              >
                {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                <span>Save Contact Info Changes</span>
              </button>
            </div>
          </div>
        )}

        {/* TAB 4: COMMUNITY FORUM */}
        {activeTab === 'forum' && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-800 border-b pb-3">Community Forum Landing Page</h2>
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Title</label>
                <input
                  type="text"
                  value={forumContent.title || ''}
                  onChange={(e) => setForumContent({ ...forumContent, title: e.target.value })}
                  placeholder="e.g. Community Forum"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Subtitle</label>
                <textarea
                  value={forumContent.subtitle || ''}
                  onChange={(e) => setForumContent({ ...forumContent, subtitle: e.target.value })}
                  placeholder="e.g. Join discussions, ask questions, and share your insights with the ShreeGuruCool community."
                  rows={4}
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300"
                />
              </div>
            </div>

            {/* Save Button */}
            <div className="pt-6 border-t flex justify-end">
              <button
                type="button"
                onClick={() => handleSave('forum')}
                disabled={isSaving}
                className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold px-8 py-3 rounded-xl shadow-lg hover:shadow-blue-500/25 transition-all duration-300 flex items-center justify-center gap-2 transform hover:-translate-y-0.5"
              >
                {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                <span>Save Forum Changes</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManagePages;
